import { useCallback, useMemo, useState } from "react";

import Card from "ds/components/Card";
import Typography from "ds/components/Typography";
import MarkdownAssistantPanel from "components/markdown/MarkdownAssistantPanel";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Banner from "ds/components/Banner";
import { ConfettiColored } from "components/icons/generated";
import Icon from "ds/components/Icon";
import ProgressBar from "ds/components/ProgressBar";
import TextEllipsis from "ds/components/TextEllipsis";
import useUserGuideProgressMutations from "shared/UserGuides/useUserGuideProgressMutations";
import {
  USER_GUIDE_RESTART_EVENT_NAME,
  USER_GUIDE_START_EVENT_NAME,
  UserGuideStartLocation,
} from "shared/UserGuides/analytics";
import { getErrorMessage } from "shared/UserGuides/utils";
import { CompletedGuideData } from "components/AssistantPanel/Learn/types";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageUserGuides } from "hooks/useAnalytics/pages/userGuides";
import {
  getHardcodedUserGuideGroupIconAndColor,
  randomIconAndColor,
} from "shared/UserGuides/mocks";
import FeedbackButton from "components/FeedbackButton";

import useAssistantPanelLearnStore from "../../../useStore";
import styles from "./styles.module.css";

type AssistantPanelLearnActiveGuideViewCompletionStepProps = {
  data: CompletedGuideData;
};

const AssistantPanelLearnActiveGuideViewCompletionStep = ({
  data,
}: AssistantPanelLearnActiveGuideViewCompletionStepProps) => {
  const { setNavigationState, navigationState } = useAssistantPanelLearnStore();
  const { startUserGuide, restartUserGuide } = useUserGuideProgressMutations({
    propagateError: true,
    suppressFlashError: true,
  });

  const [isStartingNextGuide, setIsStartingNextGuide] = useState(false);
  const [stepErrorMessage, setStepErrorMessage] = useState<string>();

  const trackSegmentEvent = useAnalytics();

  const handleFinish = useCallback(() => {
    if (navigationState.level === "guide") {
      setNavigationState({ level: "chapters", groupId: navigationState.groupId });
    } else {
      setNavigationState({ level: "groups" });
    }
  }, [setNavigationState, navigationState]);

  const handleStartNextGuide = useCallback(async () => {
    if (!data.nextGuideId) return;

    setStepErrorMessage(undefined);
    setIsStartingNextGuide(true);
    trackSegmentEvent(
      data.isNextGuideCompleted ? USER_GUIDE_RESTART_EVENT_NAME : USER_GUIDE_START_EVENT_NAME,
      { location: UserGuideStartLocation.AssistantPanel }
    );
    try {
      if (data.isNextGuideCompleted) {
        await restartUserGuide({ id: data.nextGuideId });
      } else {
        await startUserGuide({ id: data.nextGuideId });
      }
    } catch (error) {
      setStepErrorMessage(getErrorMessage(error));
    } finally {
      setIsStartingNextGuide(false);
    }
  }, [
    data.nextGuideId,
    data.isNextGuideCompleted,
    startUserGuide,
    restartUserGuide,
    trackSegmentEvent,
  ]);

  const icon = useMemo(() => {
    if (navigationState.level === "guide" || navigationState.level === "chapters") {
      const { groupId } = navigationState;
      if (groupId) {
        return getHardcodedUserGuideGroupIconAndColor(groupId).icon;
      }
    }

    // if from some reason we are in the groups level, fallback to a random icon and color
    return randomIconAndColor().icon;
  }, [navigationState]);

  return (
    <Box direction="column" gap="large" grow="1" padding="x-large" scrollable>
      <Box align="center" justify="between" fullWidth gap="medium">
        <Box align="center" gap="small" className={styles.guideTitle} padding="small">
          <Icon src={icon} />
          <TextEllipsis tooltip={data.guideTitle}>
            {(props) => (
              <Typography {...props} tag="span" variant="p-body4">
                {data.guideTitle}
              </Typography>
            )}
          </TextEllipsis>
        </Box>

        <Box grow="1" align="center" gap="medium" className={styles.progressBar}>
          <ProgressBar
            current={data.totalSteps}
            max={data.totalSteps}
            fullWidth
            ariaLabel="Guide progress"
          />
        </Box>
      </Box>

      <Card contentGap="large" className={styles.card}>
        <Icon src={ConfettiColored} size="x-large" />

        {stepErrorMessage && <Banner variant="danger">{stepErrorMessage}</Banner>}

        <Typography tag="div" variant="p-body3">
          <MarkdownAssistantPanel>{data.completionMessage}</MarkdownAssistantPanel>
        </Typography>

        <Box direction="row" gap="medium" align="center">
          <Button
            variant={data.nextGuideId ? "secondary" : "primary"}
            size="small"
            onPress={handleFinish}
            disabled={isStartingNextGuide}
            analyticsPage={AnalyticsPageUserGuides.UserGuides}
            analyticsTitle="Finish Guide Clicked"
            analyticsProps={{
              button: "Finish",
              userGuideName: data.guideTitle,
            }}
          >
            Finish
          </Button>
          {data.nextGuideId && (
            <Button
              variant="primary"
              size="small"
              onPress={handleStartNextGuide}
              disabled={isStartingNextGuide}
              loading={isStartingNextGuide}
              analyticsPage={AnalyticsPageUserGuides.UserGuides}
              analyticsTitle="Next objective Clicked"
              analyticsProps={{
                button: "Next objective",
                userGuideName: data.guideTitle,
              }}
            >
              Next objective
            </Button>
          )}
        </Box>

        <Box className={styles.feedbackRow} align="center">
          <FeedbackButton id="user-guide-completion-feedback" variant="ghost" size="small" />
        </Box>
      </Card>
    </Box>
  );
};

export default AssistantPanelLearnActiveGuideViewCompletionStep;
